<template>
  <div class="providers-list">
    <h1 class="listing-titles">
      {{ t("PaymentsProvidersView-HowToPay") }}
    </h1>
    <template v-if="filteredProviders[0].length">
      <ul>
        <li
          v-for="provider in filteredProviders[0]"
          :key="provider.name"
        >
          <CardItem
            :logo-src="provider.logo"
            :logo-alt="provider.displayName"
            :title="t(provider.displayName)"
            :subtitle="provider.tooltipPrefix ? t(provider.tooltipPrefix) : ''"
            :right-text="computeRightText(provider)"
            :inactive="
              provider.featureFlagStatus?.disabled === true ||
              (country === 'PE' && isOtherAmountSelected && !otherAmountAllowedProviders.includes(provider.name))
            "
            @click="handleSelectPaymentProvider(provider)"
          />
          <p v-if="provider.featureFlagStatus?.disabled === true" class="payment-provider-feature-flag-message">
            {{ provider.featureFlagStatus?.title }}
          </p>
        </li>
      </ul>
    </template>
    <template v-if="filteredProviders[1].length">
      <ul>
        <li
          v-for="provider in filteredProviders[1]"
          :key="provider.name"
        >
          <CardItem
            :logo-src="provider.logo"
            :logo-alt="provider.displayName"
            :title="t(provider.displayName)"
            :subtitle="provider.tooltipPrefix ? t(provider.tooltipPrefix) : ''"
            :right-text="computeRightText(provider)"
            :inactive="
              provider.featureFlagStatus?.disabled === true ||
              (country === 'PE' && isOtherAmountSelected && !otherAmountAllowedProviders.includes(provider.name))
            "
            @click="handleSelectPaymentProvider(provider)"
          />
          <p v-if="provider.featureFlagStatus?.disabled === true" class="payment-provider-feature-flag-message">
            {{ provider.featureFlagStatus?.title }}
          </p>
        </li>
      </ul>
    </template>
  </div>
  <BottomBar
    :text="t('PaymentsProvidersView-PaymentAmount')"
    :amount="amount"
    :term-label="term"
    @change-payment-amount="handleAmountChange"
  />
  <HighlightPopup :is-visible="onHighlighted" @close-popup="onClosePopup('highLight')" @dismiss-popup="onDismissPopup">
    <h1>{{ t("Popup-Pay-Without-Commissions") }}</h1>
    <h2>{{ t("Popup-Save-Money-Using-These-Options") }}</h2>
    <ul class="savings-container">
      <li v-for="provider in highlightedProviders" :key="t(provider.name)" class="savings">
        <img :src="provider.logo" :alt="provider.displayName" />
      </li>
    </ul>
  </HighlightPopup>

  <HighlightPopup
    v-if="pixProvider"
    :is-visible="showPixIsFasterPopup"
    @close-popup="onClosePopup('pixPopup')"
    @dismiss-popup="onDismissPopup"
  >
    <img :src="pixProvider.logo" :alt="pixProvider.displayName" />
    <h1 class="text-black">
      {{ t("Popup-Pix-Faster") }}
    </h1>
    <h2>
      {{ t("Popup-Pix-Compare-To-Boleto") }}
    </h2>
  </HighlightPopup>

  <HighlightPopup
    :is-visible="showPix10MinutePopup"
    :button-text="t('continue')"
    @close-popup="onClosePopup('pix10MinutePopup')"
    @dismiss-popup="onDismissPopup"
  >
    <img src="@/assets/icons/green_bell.svg" alt="Info" />
    <h1 class="text-black">
      {{ t("Popup-Pix-First-Time-Title") }}
    </h1>
    <h2>{{ t("Popup-Pix-First-Time-Description") }}</h2>
  </HighlightPopup>

  <HighlightPopup
    v-if="pixProvider"
    id="boletoPopup"
    :is-visible="showBoletoPopup"
    :bottom-link-text="t('try-with-pix')"
    :button-text="boletoPopupButtonText"
    @bottom-link-click="tryPix()"
    @close-popup="onClosePopup('boletoPopup')"
    @dismiss-popup="onDismissPopup"
  >
    <img src="@/assets/icons/warn.svg" alt="Warning" />
    <h1 class="text-black">
      {{ boletoPopupTitle }}
    </h1>
    <h2>
      {{ boletoPopupDescription }}
    </h2>
  </HighlightPopup>

  <HighlightPopup
    :is-visible="detailsPopupObject !== undefined"
    :button-text="detailsPopupObject?.disabled == true ? t('close') : t('continue')"
    :bottom-link-text="detailsPopupObject?.disabled == true ? undefined : t('cancel')"
    @close-popup="onClosePopup('details')"
    @dismiss-popup="onDismissPopup"
    @bottom-link-click="onDismissPopup"
  >
    <div>
      <div><img src="@/assets/icons/warn.svg" height="67" alt="Info" /></div>
      <h1>{{ detailsPopupObject?.title }}</h1>
      <p>{{ detailsPopupObject?.banner }}</p>
    </div>
  </HighlightPopup>

  <HighlightPopup
    :is-visible="
      !!providers.globalWarning.value?.title && !!providers.globalWarning.value?.banner && !globalWarningDismissed
    "
    :button-text="t('close')"
    @close-popup="dismissGlobalWarning"
    @dismiss-popup="dismissGlobalWarning"
  >
    <div>
      <div><img src="@/assets/icons/warn.svg" height="67" alt="Info" /></div>
      <h1>{{ providers.globalWarning.value?.title }}</h1>
      <p>{{ providers.globalWarning.value?.banner }}</p>
    </div>
  </HighlightPopup>

  <HighlightPopup
    :is-visible="showOtherAmountPopup"
    :button-text="t('close')"
    @close-popup="onDismissPopup"
    @dismiss-popup="onDismissPopup"
  >
    <div>
      <div><img src="@/assets/icons/warn.svg" height="67" alt="Info" /></div>
      <h1>{{ t("attention") }}</h1>
      <p>{{ t("other-amount-qr-content") }}</p>
    </div>
  </HighlightPopup>
</template>

<script lang="ts">
import { computed, defineComponent, ref, Ref, UnwrapRef } from "vue";
import { usePaymentProviders } from "@/services/DataService";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import router from "@/router";
import CardItem from "@/components/CardItem.vue";
import BottomBar from "@/components/BottomBar.vue";
import { AnalyticEventsNames } from "@/helpers/google-analytics.helpers";
import HighlightPopup from "@/components/HighlightPopup.vue";
import { IInstallmentDetail } from "@/types/IInstallment";
import { getCookie, setCookie } from "@/helpers/cookieHelper";
import authHelper from "@/helpers/authHelper";
import { ActionLogTypeConst } from "@/types/IActionLogs";
import prefetchHelper from "@/helpers/prefetchHelper";
import { Countries } from "@/types/Countries";
import { Providers } from "@/types/Providers";
import { computeTermLabel } from "@/shared-helpers/language.helper";
import { IFeatureFlagLogType, IGlobalWarning, IPaymentProvider, IPaymentProviderType } from "@/types/IPaymentProvider";
import { FinanceProductTypes, CustomerNationalIdTypes } from "@/types/ISuperPacman";

export default defineComponent({
  name: "PaymentProvidersView",
  components: { BottomBar, CardItem, HighlightPopup},
  props: {
    usingSP: {
      type: String,
      required: false,
      default: "false",
    },
  },
  emits: ["select-payment-provider", "change-payment-amount", "close-popup"],
  setup() {
    const { t, te } = useI18n();
    const router = useRouter();

    const store = useStore();
    const country = computed(() => store.state.payOptions.deviceInfo.country);
    const deviceTag = computed(() => store.state.payOptions.deviceInfo.deviceTag);
    const selectedInstallment = computed(() => store.state.payOptions.selectedInstallment as IInstallmentDetail);
    const amount = computed(() => selectedInstallment.value?.amount);
    const term = computed(() => computeTermLabel(t, te, selectedInstallment.value?.term));

    const paymentOption = computed(() => store.state.payOptions.deviceInfo.paymentOption);
    const providers: {
      paymentProviders: Ref<UnwrapRef<IPaymentProvider[]>>;
      globalWarning: Ref<UnwrapRef<IGlobalWarning | null>>;
    } = usePaymentProviders({
      country: country.value,
      paymentOption: paymentOption.value,
    });
    const globalWarningDismissed = ref(false);

    const filteredProviders = computed(() => {
      let resultProviders: IPaymentProvider[] = [];

      // First let's check if the country is set and remove inactive providers
      if (country.value) {
        resultProviders = providers.paymentProviders.value.filter((provider) => provider.country === country.value);
      } else {
        resultProviders = providers.paymentProviders.value;
      }
      resultProviders = resultProviders.filter((provider) => !provider.showGrouped).sort((a, b) => a.order - b.order);

      let providersWithZeroFee = resultProviders.filter((provider) => provider.fee === 0);
      let providersWithNonZeroFee = resultProviders.filter((provider) => provider.fee !== 0);

      if (store.state.payOptions.superPacmanInstallment) {
        if (store.state.payOptions.superPacmanInstallment.financeProduct.country === Countries.MEXICO) {
          const cumulativeBalanceEnabled =
            store.state.payOptions.superPacmanInstallment.cumulativeBalance?.enabled ??
            store.state.payOptions.superPacmanInstallment.cumulativeBalance?.cumulativeBalanceEnabled ??
            false;
          const financeProductType = store.state.payOptions.superPacmanInstallment.financeProduct.financeProductType;
          const isOldMxFo = financeProductType === FinanceProductTypes.FEv1 && !cumulativeBalanceEnabled;
          if (isOldMxFo) {
            // Old MX FO customers (FEv1 without cumulative balance): Show STP, hide BITSO
            providersWithZeroFee = providersWithZeroFee.filter((provider) => provider.name !== Providers.BITSO);
          } else {
            // All other MX customers (FEv2, or FEv1 with cumulative balance): Show BITSO, hide STP
            const bitsoExists = providersWithZeroFee.some((provider) => provider.name === Providers.BITSO);
            if (bitsoExists) {
              providersWithZeroFee = providersWithZeroFee.filter((provider) => provider.name !== Providers.STP);
            }
          }
        }
      } else {
        providersWithZeroFee = providersWithZeroFee.filter((provider) => provider.name !== Providers.BITSO);
      }

      // VPOP-459: Show Interbank for PE customers with National ID only
      if (
        store.state.payOptions.superPacmanInstallment &&
        store.state.payOptions.superPacmanInstallment.financeProduct.country === Countries.PERU &&
        store.state.payOptions.superPacmanInstallment.financeProduct.customerNationalIdType !==
          CustomerNationalIdTypes.NationalId
      ) {
        providersWithZeroFee = providersWithZeroFee.filter((provider) => provider.name !== Providers.INTERBANK);
        providersWithNonZeroFee = providersWithNonZeroFee.filter((provider) => provider.name !== Providers.INTERBANK);
      }

      // PYMT-1937: Show BCP and Kasnet only for PE customers with Residence Permit or National ID type
      if (
        store.state.payOptions.superPacmanInstallment &&
        store.state.payOptions.superPacmanInstallment.financeProduct.country === Countries.PERU &&
        store.state.payOptions.superPacmanInstallment.financeProduct.customerNationalIdType !==
          CustomerNationalIdTypes.ResidencePermit &&
        store.state.payOptions.superPacmanInstallment.financeProduct.customerNationalIdType !==
          CustomerNationalIdTypes.NationalId
      ) {
        providersWithZeroFee = providersWithZeroFee.filter(
          (provider) => provider.name !== Providers.BCP && provider.name !== Providers.KASNET,
        );
        providersWithNonZeroFee = providersWithNonZeroFee.filter(
          (provider) => provider.name !== Providers.BCP && provider.name !== Providers.KASNET,
        );
      }

      // VPOP-426: in Ecuador, providers are grouped based on if the payment can be made in-store or online, not by fees
      if (country.value === Countries.ECUADOR) {
        providersWithZeroFee = resultProviders.filter((provider) => provider.type === IPaymentProviderType.InStoreCash);
        providersWithNonZeroFee = resultProviders.filter((provider) =>
          [IPaymentProviderType.OnlinePayment, IPaymentProviderType.BankTransfer].includes(provider.type),
        );
      } else if (country.value === Countries.INDONESIA && store.state.payOptions.superPacmanInstallment) {
        // VPOP-441: For Indonesia, filter providers based on subProductVersion
        const subProductVersion = store.state.payOptions.superPacmanInstallment.financeProduct.subProductVersion;
        if (subProductVersion === "BSS") {
          providersWithZeroFee = providersWithZeroFee.filter((provider) => provider.name === Providers.VIRTUAL_ACCOUNT);
          providersWithNonZeroFee = providersWithNonZeroFee.filter(
            (provider) => provider.name === Providers.VIRTUAL_ACCOUNT,
          );
        } else if (subProductVersion === "Rental") {
          providersWithZeroFee = providersWithZeroFee.filter((provider) => provider.name === Providers.XENDIT);
          providersWithNonZeroFee = providersWithNonZeroFee.filter((provider) => provider.name === Providers.XENDIT);
        } else {
          providersWithZeroFee = [];
          providersWithNonZeroFee = [];
        }
      }

      return [providersWithZeroFee, providersWithNonZeroFee];
    });

    const highlightedProviders = computed(() => {
      return providers.paymentProviders.value.filter((provider) => provider.highlight);
    });

    // for Brazil only, to highlight a Pix popup the first time user visits this page
    const pixProvider: Ref<IPaymentProvider | null> = computed(() => {
      if (country.value === Countries.BRAZIL) {
        let result: IPaymentProvider | null = providers.paymentProviders.value.filter(
          (provider) => provider.featureFlagStatus?.disabled !== true && provider.name === Providers.ASAAS_PIX,
        )[0];

        if (!result) {
          // to ensure that if result is empty, pixProvider equals null
          result = null;
        }
        return result;
      } else {
        return null;
      }
    });

    const logoList = computed(() => {
      return Object.values(filteredProviders.value)
        .flat()
        .map((provider: IPaymentProvider) => provider.logo);
    });

    const detailsPopupObject = ref<IFeatureFlagLogType | undefined>();
    const pendingProvider = ref<IPaymentProvider | undefined>();
    const boletoPopupTitle = ref(t("Popup-Boleto-First-Time-Title"));
    const boletoPopupDescription = ref(t("Popup-Boleto-First-Time-Description"));
    const boletoPopupButtonText = ref(t("continue"));
    const dismissGlobalWarning = () => {
      globalWarningDismissed.value = true;
    };

    const computeRightText = (provider: IPaymentProvider): string => {
      if (country.value === Countries.ECUADOR) {
        return provider.type === IPaymentProviderType.InStoreCash
          ? t("PaymentsProvidersView-InStorePayment")
          : t("PaymentsProvidersView-OnlinePayment");
      }
      if (provider.fee === 0) return t("no-fee");
      return provider.toolTip ? t(provider.toolTip) : `+${provider.fee}`;
    };

    const isOtherAmountSelected = computed(() => store.state.payOptions.isOtherAmountSelected as boolean);
    const otherAmountAllowedProviders: string[] = [Providers.KASNET_QR, "PAYJOY"];
    // PYMT-1994: Indonesia Rental customers can pay any custom amount via Xendit,
    // so the "Other Amount" restriction popup should not block them.
    const isIndonesiaRental = computed(
      () =>
        country.value === Countries.INDONESIA &&
        store.state.payOptions.superPacmanInstallment?.financeProduct.subProductVersion === "Rental",
    );

    return {
      t,
      providers,
      filteredProviders,
      highlightedProviders,
      router,
      country,
      deviceTag,
      amount,
      globalWarningDismissed,
      dismissGlobalWarning,
      selectedInstallment,
      paymentOption,
      pixProvider,
      logoList,
      detailsPopupObject,
      pendingProvider,
      term,
      store,
      computeRightText,
      boletoPopupTitle,
      boletoPopupDescription,
      boletoPopupButtonText,
      Providers,
      isOtherAmountSelected,
      otherAmountAllowedProviders,
      isIndonesiaRental,
    };
  },
  data() {
    return {
      onHighlighted: true,
      showWarningPopUp: false,
      showPixIsFasterPopup: false,
      showPix10MinutePopup: false,
      showBoletoPopup: false,
      showOtherAmountPopup: false,
    };
  },
  watch: {
    logoList: {
      handler(logoList) {
        if (logoList.length > 0) prefetchHelper.prefetchImages(logoList);
      },
      immediate: true,
    },
  },
  mounted() {
    // Check if the user has visited this page before
    const hasVisited = getCookie("hasVisitedPaymentProviders");
    if (hasVisited) {
      this.onHighlighted = false;
    } else {
      setCookie("hasVisitedPaymentProviders", "true", 365);

      // if the user is from Brazil, show instead the Pix popup
      if (this.country === Countries.BRAZIL) {
        this.showPixIsFasterPopup = true;
      } else if ([Countries.INDONESIA, Countries.PERU].includes(this.country)) {
        this.onHighlighted = false;
      }

      // the pay without commissions popup should work only for MX
      this.onHighlighted = this.country === Countries.MEXICO;
    }

    if (this.isOtherAmountSelected && this.country === Countries.PERU) {
      this.showOtherAmountPopup = true;
    }

    if (process.env.VUE_APP_USE_ACTION_LOGS === "true") {
      // Log the event on page view
      authHelper.logAction(
        {
          deviceTag: this.deviceTag,
          country: this.country,
          action: AnalyticEventsNames.OnSelectPaymentProviderPage,
          actionType: ActionLogTypeConst.PAGE_VIEW,
        },
        this.$gtag,
      );
    }

    // Is on highlight PopUp? log the event
    if (this.onHighlighted) {
      if (process.env.VUE_APP_USE_ACTION_LOGS === "true") {
        authHelper.logAction(
          {
            deviceTag: this.deviceTag,
            country: this.country,
            action: AnalyticEventsNames.OnSelectPaymentProviderPage_CommissionsModalOpened,
            actionType: ActionLogTypeConst.PAGE_VIEW,
          },
          this.$gtag,
        );
      }
    }
  },
  beforeUnmount() {
    if (process.env.VUE_APP_USE_ACTION_LOGS === "true") {
      // Log the event on page unmount
      authHelper.logAction(
        {
          deviceTag: this.deviceTag,
          country: this.country,
          action: AnalyticEventsNames.OnSelectPaymentProviderPage_BrowserClose,
          actionType: ActionLogTypeConst.PAGE_VIEW,
        },
        this.$gtag,
      );
    }

    this.router.beforeEach((to, from, next) => {
      if (to.name === "InstallmentsByDevice") {
        if (process.env.VUE_APP_USE_ACTION_LOGS === "true") {
          //Go back to InstallmentsByDevice, so log the action
          authHelper.logAction(
            {
              deviceTag: this.deviceTag,
              country: this.country,
              action: AnalyticEventsNames.OnSelectPaymentProviderPage_BrowserGoBack,
              actionType: ActionLogTypeConst.BUTTON_CLICK,
            },
            this.$gtag,
          );
        }
      }
      next();
    });
  },
  methods: {
    handleSelectPaymentProvider(selectedPaymentProvider: IPaymentProvider) {
      if (this.country === Countries.PERU && this.isOtherAmountSelected && !this.otherAmountAllowedProviders.includes(selectedPaymentProvider.name)) {
        this.showOtherAmountPopup = true;
        return;
      }
      const { disabled, title, banner } = selectedPaymentProvider.featureFlagStatus ?? {};
      if (disabled === true && !title && !banner) {
        return;
      }
      if (title || banner) {
        this.detailsPopupObject = selectedPaymentProvider.featureFlagStatus;
        this.pendingProvider = selectedPaymentProvider;
        return;
      }
      this.proceedWithProvider(selectedPaymentProvider);
    },
    proceedWithProvider(selectedPaymentProvider: IPaymentProvider) {
      this.$emit("select-payment-provider", selectedPaymentProvider.name);

      if (process.env.VUE_APP_USE_ACTION_LOGS === "true") {
        authHelper.logAction(
          {
            deviceTag: this.deviceTag,
            country: this.country,
            action: AnalyticEventsNames.OnSelectPaymentProviderPage_SelectPaymentProvider,
            actionType: ActionLogTypeConst.BUTTON_CLICK,
            data: {
              deviceTag: this.deviceTag,
              country: this.country,
              provider: selectedPaymentProvider.name,
            },
          },
          this.$gtag,
        );
      }

      // if the country is Brazil, show a modal every time the user clicks on each payment method
      if (this.country === Countries.BRAZIL) {
        if (selectedPaymentProvider.name === Providers.ASAAS_PIX) {
          this.showPix10MinutePopup = true;
        } else if (selectedPaymentProvider.name === Providers.ASAAS_BOLETO) {
          this.showBoletoPopup = true;
        }
        return;
      }

      router.push({
        name: "DynamicProviderLoader",
        params: {
          providerName: selectedPaymentProvider.name,
          displayName: selectedPaymentProvider.displayName,
          type: selectedPaymentProvider.type,
        },
      });
    },
    handleAmountChange() {
      if (process.env.VUE_APP_USE_ACTION_LOGS === "true") {
        authHelper.logAction(
          {
            deviceTag: this.deviceTag,
            country: this.country,
            action: AnalyticEventsNames.OnSelectPaymentProviderPage_ChangeInstallment,
            actionType: ActionLogTypeConst.BUTTON_CLICK,
          },
          this.$gtag,
        );
      }
      if (this.store.state.payOptions.superPacmanInstallment) {
        this.router.push("/p/device/" + this.deviceTag + "?showoptions=" + this.paymentOption + "&forceUpdate=true");
      } else {
        this.router.push("/device/" + this.deviceTag + "?showoptions=" + this.paymentOption + "&forceUpdate=true");
      }
    },
    onDismissPopup() {
      this.detailsPopupObject = undefined;
      this.showBoletoPopup = false;
      this.showPix10MinutePopup = false;
      this.showOtherAmountPopup = false;
    },
    onClosePopup(source: string) {
      this.$emit("close-popup");

      switch (source) {
        case "details":
          this.detailsPopupObject = undefined;
          const provider = this.pendingProvider;
          this.pendingProvider = undefined;
          if (provider && provider.featureFlagStatus?.disabled !== true) {
            this.proceedWithProvider(provider);
          }
          break;

        case "highLight":
          this.onHighlighted = false;
          if (process.env.VUE_APP_USE_ACTION_LOGS === "true") {
            authHelper.logAction(
              {
                deviceTag: this.deviceTag,
                country: this.country,
                action: AnalyticEventsNames.OnSelectPaymentProviderPage_CloseCommissionsModal,
                actionType: ActionLogTypeConst.BUTTON_CLICK,
              },
              this.$gtag,
            );
          }
          break;

        case "pixPopup":
          this.showPixIsFasterPopup = false;
          break;

        case "pix10MinutePopup":
          this.showPix10MinutePopup = false;

          if (this.pixProvider !== null) {
            router.push({
              name: "DynamicProviderLoader",
              params: { providerName: Providers.ASAAS_PIX },
            });
          }
          break;

        case "boletoPopup":
          this.showBoletoPopup = false;

          // customers can pay with Boleto, only if the chosen amount >= R$5
          if (this.amount >= 5) {
            router.push({
              name: "DynamicProviderLoader",
              params: { providerName: Providers.ASAAS_BOLETO },
            });
          } else if (this.boletoPopupButtonText === this.t("close")) {
            // reset the Boleto modal to it's initial content
            this.boletoPopupTitle = this.t("Popup-Boleto-First-Time-Title");
            this.boletoPopupDescription = this.t("Popup-Boleto-First-Time-Description");
            this.boletoPopupButtonText = this.t("continue");
          } else {
            // if amount is less than R$5, the customer cannot pay with Boleto. Must pay with PIX
            this.boletoPopupTitle = this.t("Popup-Boleto-R5-Title");
            this.boletoPopupDescription = this.t("Popup-Boleto-R5-Description");
            this.boletoPopupButtonText = this.t("close");

            setTimeout(() => {
              this.showBoletoPopup = true;
            }, 100);
          }
          break;

        default:
          this.showWarningPopUp = false;
          this.detailsPopupObject = undefined;
          break;
      }
    },
    tryPix() {
      const hasVisitedPixProvider = getCookie("hasVisitedPixProvider");

      if (!hasVisitedPixProvider) {
        setCookie("hasVisitedPixProvider", "true", 365);
        this.showBoletoPopup = false;
        this.showPix10MinutePopup = true;
        return;
      }

      router.push({
        name: "DynamicProviderLoader",
        params: { providerName: Providers.ASAAS_PIX },
      });
    },
  },
});
</script>

<style scoped>
.providers-list {
  padding: 16px 16px 128px 16px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.providers-list h1 {
  color: #333;
  font-family: "Inter", sans-serif;
  font-size: 22px;
  font-style: normal;
  font-weight: 700;
  line-height: 28px;
  letter-spacing: -0.26px;
  margin: 0;
}

h3 {
  margin: 16px 0 0 0;
}

.providers-list ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.providers-list ul > li {
  position: relative;
}

.payment-provider-feature-flag-message {
  margin-top: 5px;
  margin-left: 5px;
  color: var(--text-grey);
  text-align: left;
  font-feature-settings:
    "liga" off,
    "clig" off;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
}

.message h1 {
  color: var(--Primary-Green-1);
  text-align: center;
  font-feature-settings: "liga" off, "clig" off;
  font-size: 20px;
  font-weight: 600;
  line-height: 24px; /* 120% */
}

.message h2 {
  color: var(--Typography-Text-2);
  font-feature-settings:
    "clig" off,
    "liga" off;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px; /* 150% */
}

.savings-container {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  max-width: 230px;
  margin-left: auto;
  margin-right: auto;
  list-style: none;
  padding: 0;
}

.savings {
  background: #fff;
  display: flex;
}

.savings img {
  margin: auto;
  min-width: 50px;
  max-width: 70px;
}
</style>
